import { createAjaxAction } from '../../../Common/_actions/AjaxAction';
import { InvalidatePaginationData } from '../UserDataActions';
import ApiErrorHandler from '../../../Common/Error/ApiErrorHandler';
import { EditStore, RemoveStoreBySlug } from './StoreStoreActions';
import { EditPurchaseReportData } from './StorePurchaseReportActions';
import { type PurchaseReportPayload } from '../../_stores/Store/PurchaseReportStore';
import { type UserStorePayload } from '../../_stores/Store/UserStoreStore';

export enum StoreActionType {
  FetchStoreData = 'STORE_FETCH_STORE_DATA',
  FetchStoreOverview = 'STORE_FETCH_OVERVIEW',
  FetchPurchaseReportData = 'STORE_FETCH_PURCHASE_REPORT_DATA',
  RefreshStoreOrders = 'STORE_REFRESH_ORDERS',
  RefreshStoreProducts = 'STORE_REFRESH_PRODUCTS',
  SaveStoreSettings = 'STORE_SAVE_SETTINGS',
  DeleteStore = 'STORE_DELETE_STORE',
  CompleteStoreRating = 'STORE_COMPLETE_RATING',
}

export type StoreOverviewPayload = {
  summary: {
    orders: number
    productsSold: number
    cost: number
    profit: number
  }
  ordersOverview: {
    awaitingPayment: number
    paymentProcessed: number
    inProduction: number
    shipped: number
  }
  actionRequiredOrders: Array<{
    id: number
    refId: string
    status: 'ordered' | 'on-hold'
    date: string | null
    name: string
    country: string
    total: number
    productsSold: number
    thumbnails: string[]
  }>
  productTypeSales: Array<{
    label: string
    value: number
  }>
  lastProductSales: Array<{
    title: string
    preview: string
    location: string
    refId: string
  }>
  showRatingBadge: boolean
}

export const FetchStoreData = (store: string) => createAjaxAction<{store: UserStorePayload}>(StoreActionType.FetchStoreData, {
  url: 'ajax/api/user/stores/' + store,
  method: 'GET',
  abortPrevious: true,
  onSuccess: (response, dispatch) => {
    dispatch(EditStore(response.data.store));
  },
  onFailure: ApiErrorHandler(StoreActionType.FetchStoreData),
})

export const FetchStoreOverview = (store: string, period: string) => createAjaxAction<StoreOverviewPayload>(StoreActionType.FetchStoreOverview, {
  url: 'ajax/api/user/stores/' + store + '/overview',
  method: 'GET',
  data: {
    period: period,
  },
})

export const FetchPurchaseReportData = (store: string, startDate: string, endDate: string) => createAjaxAction<{report: PurchaseReportPayload}>(StoreActionType.FetchPurchaseReportData, {
  url: 'ajax/api/user/stores/' + store + '/purchase-report',
  method: 'GET',
  data: {
    startDate: startDate,
    endDate: endDate,
  },
  onSuccess: (response, dispatch) => {
    dispatch(EditPurchaseReportData(store, response.data.report));
  },
})

export const RefreshStoreProducts = (store: string) => createAjaxAction<{}>(StoreActionType.RefreshStoreProducts, {
  url: 'ajax/api/user/stores/' + store + '/refresh-products',
  method: 'POST',
  onSuccess: (response, dispatch) => {
    dispatch(FetchStoreData(store));
  },
  onFailure: ApiErrorHandler(StoreActionType.RefreshStoreProducts),
})

export const RefreshStoreOrders = (store: string) => createAjaxAction<{}>(StoreActionType.RefreshStoreOrders, {
  url: 'ajax/api/user/stores/' + store + '/refresh-orders',
  method: 'POST',
  onSuccess: (response, dispatch) => {
    dispatch(InvalidatePaginationData('orders'))
    dispatch(FetchStoreData(store));
  },
  onFailure: ApiErrorHandler(StoreActionType.RefreshStoreOrders),
})

export const SaveStoreSettings = (
  store: string,
  autopay: boolean,
  payment_method: string,
  billing_address: number | null,
  autocancel: boolean,
  autoship: boolean,
  import_unsynced: boolean,
) => {
  const data = new FormData()
  data.append('autopay', autopay ? '1' : '0');
  data.append('payment_method', payment_method);

  if(billing_address) {
    data.append('billing_address', String(billing_address));
  }

  data.append('autocancel', autocancel ? '1' : '0');
  data.append('autoship', autoship ? '1' : '0');
  data.append('import_unsynced', import_unsynced ? '1' : '0');

  return createAjaxAction<{}>(StoreActionType.SaveStoreSettings, {
    url: 'ajax/api/user/stores/' + store + '/save-settings',
    method: 'POST',
    data: data,
    onSuccess: (response, dispatch) => {
      dispatch(FetchStoreData(store));
    },
    onFailure: ApiErrorHandler(StoreActionType.SaveStoreSettings),
  })
};

export const DeleteStore = (
  store: string,
  password: string
) => {
  const data = new FormData()
  data.append('password', password)

  return createAjaxAction<{}>(StoreActionType.DeleteStore, {
    url: 'ajax/api/user/stores/' + store + '/delete-store',
    method: 'POST',
    data: data,
    onSuccess: (response, dispatch) => {
      dispatch(RemoveStoreBySlug(store));
    },
    onFailure: ApiErrorHandler(StoreActionType.DeleteStore),
  })
};

export const CompleteStoreRating = (
  store: string,
  answer: boolean
) => {
  const data = new FormData()
  data.append('answer', answer ? '1' : '0')

  return createAjaxAction<{}>(StoreActionType.CompleteStoreRating, {
    url: 'ajax/api/user/stores/' + store + '/complete-rating',
    method: 'POST',
    data: data,
    onFailure: ApiErrorHandler(StoreActionType.CompleteStoreRating),
  })
}
